import { useEffect, useMemo, useState } from 'react';

import { OfferFragment, useOffersQuery, LocationsGeoFilterInput } from '@ocx/graphql';
import { useMembership } from '@ocx/data-membership';
import { mapFragmentsToSortedOffersList } from './offer.dto';
import { OPTIMIZED_HOME_OFFERS_FILTER, OPTIMIZED_VOUCHERED_COUNT_FILTER } from './offers.constants';

import { currentCustomerCache } from './offer.cache';
import { onCacheLoadFailed } from '../cache-layer/utils';
import { useConfiguration } from '../configuration/useConfiguration';

type UseOffersProps = {
  locationsGeoFilter?: LocationsGeoFilterInput;
};

export const useOffers = ({ locationsGeoFilter }: UseOffersProps = {}) => {
  const { homePageOffersLimit } = useConfiguration('loyaltyProgram');
  const { membership } = useMembership();
  const [fragments, setFragments] = useState<OfferFragment[]>([]);
  const { data, loading, refetch } = useOffersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      ...OPTIMIZED_HOME_OFFERS_FILTER,
      first: homePageOffersLimit,
      voucherFilter: OPTIMIZED_VOUCHERED_COUNT_FILTER,
      ...(locationsGeoFilter && { locationsGeoFilter }),
    },
  });

  useEffect(() => {
    let shouldHandle = true;
    currentCustomerCache
      .getOffers()
      .then((cachedFragments) => {
        if (!shouldHandle) {
          return;
        }
        if (cachedFragments && !fragments.length) {
          setFragments(cachedFragments);
        }
      })
      .catch(onCacheLoadFailed);
    return () => {
      shouldHandle = false;
    };
  }, [fragments.length]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!data?.customer?.membership?.offers) {
      setFragments([]);
      currentCustomerCache.cacheOffers([]).catch(onCacheLoadFailed);
    } else {
      const newFragments = data.customer.membership.offers.edges
        .filter((edge): edge is { node: OfferFragment } => !!edge?.node)
        .map(({ node }) => node);
      currentCustomerCache.cacheOffers(newFragments).catch(onCacheLoadFailed);
      setFragments(newFragments);
    }
  }, [data?.customer, loading]);

  const offers = useMemo(() => {
    return mapFragmentsToSortedOffersList({
      offers: fragments,
      membershipPoints: membership.points,
    });
  }, [membership.points, fragments]);

  const rewardsQuantity = data?.customer?.membership?.vouchers?.totalCount ?? 0;

  return { offers, refetch, loading, rewardsQuantity };
};
